import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { hitosAPI } from '../services/api';

const ETAPA_LABELS = {
  "7m": "7 meses (Destete)",
  "12m": "12 meses",
  "14_15m": "14-15 meses",
  "24m": "24 meses",
  "36m": "36 meses",
  "48m": "48+ meses",
};

const ETAPA_DESC = {
  "7m": "Peso, altura cadera, peso adulto estimado",
  "12m": "Frame, crecimiento, peso/altura, CE (M), GDR (H)",
  "14_15m": "Andrologia (M), Servicio/IATF (H)",
  "24m": "1er parto (H), Libido (M)",
  "36m": "IEP definitivo, EP >=42%",
  "48m": "EP >=40%, seleccion continua",
};

const ProgressBar = ({ pct, size = "md" }) => {
  const h = size === "sm" ? "h-1.5" : "h-2.5";
  const color = pct >= 80 ? "bg-green-500" : pct >= 50 ? "bg-amber-500" : pct > 0 ? "bg-red-500" : "bg-gray-300";
  return (
    <div className={`w-full bg-gray-200 rounded-full ${h}`}>
      <div className={`${color} ${h} rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 2)}%` }} />
    </div>
  );
};

const HitosPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [prodData, setProdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('etapas');
  const [filtroSexo, setFiltroSexo] = useState('');
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [animalHitos, setAnimalHitos] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (user?.establecimiento_id) loadData();
  }, [user, filtroSexo]);

  const loadData = async () => {
    setLoading(true);
    const [rodeoRes, prodRes] = await Promise.allSettled([
      hitosAPI.rodeo(user.establecimiento_id, filtroSexo || undefined),
      hitosAPI.produccionAcumulada(user.establecimiento_id),
    ]);
    if (rodeoRes.status === 'fulfilled') setData(rodeoRes.value.data);
    if (prodRes.status === 'fulfilled') setProdData(prodRes.value.data);
    setLoading(false);
  };

  const viewAnimalDetail = async (animalId) => {
    setLoadingDetail(true);
    setSelectedAnimal(animalId);
    try {
      const res = await hitosAPI.animal(animalId);
      setAnimalHitos(res.data);
    } catch {}
    setLoadingDetail(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="hitos-loading">
      <div className="animate-spin w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full" />
    </div>
  );

  const etapas = data?.resumen_etapas || {};

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-5" data-testid="hitos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="hitos-title">Hitos de Evaluacion</h1>
          <p className="text-sm text-gray-500 mt-1">Seguimiento por etapa segun documento tecnico PDF</p>
        </div>
        <select value={filtroSexo} onChange={e => setFiltroSexo(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="filtro-sexo">
          <option value="">Todos</option>
          <option value="macho">Machos</option>
          <option value="hembra">Hembras</option>
        </select>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {[['etapas', 'Resumen Etapas'], ['animales', 'Por Animal'], ['produccion', 'Produccion Acumulada']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${tab === key ? 'bg-white shadow-sm text-teal-700' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid={`tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {/* TAB: Resumen Etapas */}
      {tab === 'etapas' && (
        <div className="grid gap-4" data-testid="etapas-grid">
          {Object.entries(ETAPA_LABELS).map(([key, label]) => {
            const e = etapas[key] || { total: 0, completados: 0 };
            const pct = e.total > 0 ? Math.round(e.completados / e.total * 100) : 0;
            return (
              <div key={key} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition" data-testid={`etapa-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-gray-900">{label}</h3>
                    <p className="text-xs text-gray-500">{ETAPA_DESC[key]}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900">{e.completados}</span>
                    <span className="text-sm text-gray-400">/{e.total}</span>
                    <p className="text-xs text-gray-500">{pct}% completado</p>
                  </div>
                </div>
                <ProgressBar pct={pct} />
              </div>
            );
          })}
        </div>
      )}

      {/* TAB: Por Animal */}
      {tab === 'animales' && (
        <div className="space-y-4">
          {/* Animal detail modal */}
          {selectedAnimal && animalHitos && (
            <div className="bg-white rounded-xl border-2 border-teal-200 p-5 space-y-4" data-testid="animal-detail">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">
                  VID {animalHitos.VID} — {animalHitos.sexo === 'macho' ? 'Macho' : 'Hembra'}, {animalHitos.edad_meses} meses
                </h3>
                <button onClick={() => { setSelectedAnimal(null); setAnimalHitos(null); }}
                  className="text-gray-400 hover:text-gray-600 text-sm">Cerrar</button>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-teal-700">Progreso: {animalHitos.progreso_general}</span>
                <div className="flex-1"><ProgressBar pct={animalHitos.porcentaje_completado} /></div>
                <span className="text-sm font-bold">{animalHitos.porcentaje_completado}%</span>
              </div>

              <div className="grid gap-3">
                {Object.entries(animalHitos.hitos || {}).map(([key, h]) => {
                  if (!h.aplica) return null;
                  const label = {
                    "7_meses_destete": "7 meses (Destete)",
                    "12_meses": "12 meses",
                    "14_15_meses": "14-15 meses",
                    "24_meses": "24 meses",
                    "36_meses": "36 meses",
                    "48_meses_plus": "48+ meses",
                  }[key] || key;
                  return (
                    <div key={key} className={`p-3 rounded-lg border ${h.completado ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${h.completado ? 'bg-green-500 text-white' : 'bg-amber-400 text-white'}`}>
                          {h.completado ? '\u2713' : '!'}
                        </span>
                        <span className="font-semibold text-sm text-gray-900">{label}</span>
                        {h.progreso && <span className="text-xs text-gray-500 ml-auto">{h.progreso}</span>}
                      </div>
                      <p className="text-xs text-gray-600 ml-7">{h.mensaje}</p>
                      {h.datos && Object.keys(h.datos).length > 0 && (
                        <div className="ml-7 mt-2 grid grid-cols-2 md:grid-cols-3 gap-1">
                          {Object.entries(h.datos).filter(([k, v]) => !k.startsWith('completado') && v !== null && v !== undefined).map(([k, v]) => (
                            <div key={k} className="text-xs">
                              <span className="text-gray-500">{k.replace(/_/g, ' ')}: </span>
                              <span className="font-medium text-gray-800">{typeof v === 'boolean' ? (v ? 'Si' : 'No') : typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {loadingDetail && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin w-6 h-6 border-2 border-teal-600 border-t-transparent rounded-full" />
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="animales-table">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    {['VID', 'Sexo', 'Categoria', 'Edad', 'Etapa Actual', 'Progreso', 'Detalle'].map(h => (
                      <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.animales?.map((a, i) => {
                    const etapaColor = {
                      "pre_destete": "bg-gray-100 text-gray-600",
                      "7m": "bg-blue-100 text-blue-700",
                      "12m": "bg-indigo-100 text-indigo-700",
                      "14_15m": "bg-purple-100 text-purple-700",
                      "24m": "bg-teal-100 text-teal-700",
                      "36m": "bg-amber-100 text-amber-700",
                      "48m": "bg-red-100 text-red-700",
                    };
                    return (
                      <tr key={a.animal_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                        <td className="py-2.5 px-3 font-semibold">{a.VID}</td>
                        <td className="py-2.5 px-3 text-xs">{a.sexo === 'macho' ? 'M' : 'H'}</td>
                        <td className="py-2.5 px-3 text-xs capitalize">{a.categoria || '—'}</td>
                        <td className="py-2.5 px-3">{a.edad_meses}m</td>
                        <td className="py-2.5 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${etapaColor[a.etapa_actual] || 'bg-gray-100'}`}>
                            {ETAPA_LABELS[a.etapa_actual] || a.etapa_actual}
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16"><ProgressBar pct={a.progreso_pct} size="sm" /></div>
                            <span className="text-xs font-medium">{a.hitos_completos}/{a.hitos_totales}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <button onClick={() => viewAnimalDetail(a.animal_id)}
                            className="text-teal-600 hover:text-teal-800 text-xs font-semibold"
                            data-testid={`btn-detail-${a.VID}`}>
                            Ver hitos
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB: Produccion Acumulada */}
      {tab === 'produccion' && (
        <div className="space-y-4" data-testid="produccion-tab">
          {prodData && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <p className="text-xs text-gray-500">Total vacas con destete</p>
                  <p className="text-2xl font-bold text-gray-900">{prodData.total_vacas}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                  <p className="text-xs text-gray-500">kg totales destetados</p>
                  <p className="text-2xl font-bold text-teal-700">{prodData.kg_total_rodeo?.toLocaleString('es-AR')} kg</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="produccion-table">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        {['VID', 'Categoria', 'Raza', 'Terneros', 'kg Total', 'Peso Prom Destete', 'Peso Madre', 'EP%'].map(h => (
                          <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {prodData.vacas?.map((v, i) => (
                        <tr key={v.animal_id} className={`border-b border-gray-100 hover:bg-gray-50 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
                          <td className="py-2.5 px-3 font-semibold">{v.VID}</td>
                          <td className="py-2.5 px-3 text-xs capitalize">{v.categoria || '—'}</td>
                          <td className="py-2.5 px-3 text-xs">{v.raza || '—'}</td>
                          <td className="py-2.5 px-3 font-semibold">{v.total_terneros}</td>
                          <td className="py-2.5 px-3 font-bold text-teal-700">{v.kg_total_destetados} kg</td>
                          <td className="py-2.5 px-3">{v.peso_destete_promedio} kg</td>
                          <td className="py-2.5 px-3">{v.peso_madre_actual ? `${v.peso_madre_actual} kg` : '—'}</td>
                          <td className="py-2.5 px-3">
                            {v.ep_promedio != null ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${v.ep_promedio >= 42 ? 'bg-green-100 text-green-700' : v.ep_promedio >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                {v.ep_promedio}%
                              </span>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                      {(!prodData.vacas || prodData.vacas.length === 0) && (
                        <tr><td colSpan={8} className="text-center py-8 text-gray-400">Sin datos de produccion acumulada</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default HitosPage;
